import { useEffect } from "react";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Share2, Facebook, Twitter, Linkedin, Copy, Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const ThankYou = () => {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [activated, setActivated] = useState(null); // null | true | false

  // Pull state from React Router OR fallback to sessionStorage (after PayPal redirect)
  let { name = "", amount = null, currency = "EUR", type = "one_time", anonymous = false } =
    location.state || {};

  if (!amount && searchParams.get("source") === "subscription") {
    try {
      const stash = JSON.parse(sessionStorage.getItem("fcs_pending_subscription") || "null");
      if (stash) {
        ({ name, amount, currency, type, anonymous } = stash);
      }
    } catch (_) {}
  }

  // Stripe redirect — pull stashed donor info if amount missing
  const stripeSessionId = searchParams.get("stripe_session_id");
  if (!amount && stripeSessionId) {
    try {
      const stash = JSON.parse(sessionStorage.getItem("fcs_pending_stripe") || "null");
      if (stash) {
        ({ name, amount, currency, type, anonymous } = stash);
      }
    } catch (_) {}
  }
  const [stripeStatus, setStripeStatus] = useState(null); // null | "polling" | "paid" | "failed"

  const subscriptionToken =
    searchParams.get("subscription_id") || searchParams.get("ba_token") || null;

  // If PayPal redirected us with a subscription token, activate it server-side
  useEffect(() => {
    if (searchParams.get("source") !== "subscription") return;
    let stashId = null;
    try {
      const stash = JSON.parse(sessionStorage.getItem("fcs_pending_subscription") || "null");
      stashId = stash?.subscription_id || null;
    } catch (_) {}
    const subId = searchParams.get("subscription_id") || stashId;
    if (!subId) return;
    api
      .post(`/paypal/subscriptions/${subId}/activate`)
      .then(({ data }) => setActivated(data?.status === "ACTIVE" || data?.status === "APPROVED"))
      .catch(() => setActivated(false))
      .finally(() => {
        try {
          sessionStorage.removeItem("fcs_pending_subscription");
        } catch (_) {}
      });
  }, [searchParams]);

  // SEO
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Stripe — poll the checkout session status until paid
  useEffect(() => {
    if (!stripeSessionId) return;
    setStripeStatus("polling");
    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 2000;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/stripe/checkout/status/${stripeSessionId}`);
        if (data?.completed) {
          setStripeStatus("paid");
          try { sessionStorage.removeItem("fcs_pending_stripe"); } catch (_) {}
          return;
        }
      } catch (_) {}
      if (attempts >= maxAttempts) {
        setStripeStatus("failed");
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
    return () => { cancelled = true; };
  }, [stripeSessionId]);

  const symbol = currency === "USD" ? "$" : currency === "GBP" ? "£" : "€";
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText =
    lang === "fr"
      ? `Je viens de soutenir la Fondation Cœur Solidaire pour redonner espoir et dignité aux veuves et aux orphelins. Rejoignez-moi 💛`
      : `I just supported Fondation Cœur Solidaire to restore hope and dignity for widows and orphans. Join me 💛`;

  const encoded = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const shareLinks = [
    {
      name: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encoded}`,
      color: "#1877F2",
    },
    {
      name: "Twitter",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encoded}&url=${encodedUrl}`,
      color: "#000000",
    },
    {
      name: "LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "#0A66C2",
    },
    {
      name: "WhatsApp",
      Icon: Share2,
      href: `https://wa.me/?text=${encoded}%20${encodedUrl}`,
      color: "#25D366",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success(lang === "fr" ? "Lien copié !" : "Link copied!");
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error("Erreur");
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="thank-you-page">
      <div className="container-narrow pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#002B49] text-sm font-medium transition-colors"
          data-testid="thanks-back-home"
        >
          <ArrowLeft size={16} /> {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full max-w-2xl"
        >
          {stripeStatus === "polling" && (
            <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-3 text-sm mb-6 text-center" data-testid="stripe-polling">
              {lang === "fr" ? "Vérification de votre paiement Stripe en cours…" : "Verifying your Stripe payment…"}
            </div>
          )}
          {stripeStatus === "failed" && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm mb-6 text-center" data-testid="stripe-status-failed">
              {lang === "fr"
                ? "Nous n'avons pas pu confirmer le paiement automatiquement. Vous recevrez un email dès la confirmation."
                : "We couldn't confirm payment automatically. You'll receive an email as soon as it's confirmed."}
            </div>
          )}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 rounded-full bg-[#002B49] mx-auto flex items-center justify-center mb-6 relative"
            >
              <Heart size={32} fill="#C5A059" className="text-[#C5A059]" />
              <Sparkles size={14} className="text-[#C5A059] absolute -top-1 -right-1" />
            </motion.div>

            <span className="eyebrow" data-testid="thanks-eyebrow">
              {lang === "fr" ? "Merci infiniment" : "Thank you so much"}
            </span>
            <span className="gold-line mt-4 mb-6 mx-auto" />

            <h1 className="font-serif text-4xl md:text-6xl text-[#0A192F] leading-[1.05] font-medium" data-testid="thanks-title">
              {name && !anonymous
                ? lang === "fr"
                  ? `Merci, ${name}.`
                  : `Thank you, ${name}.`
                : lang === "fr"
                  ? "Merci pour votre don."
                  : "Thank you for your gift."}
            </h1>

            {amount && (
              <p className="text-[#334155] text-lg md:text-xl mt-5 leading-relaxed" data-testid="thanks-amount-line">
                {lang === "fr" ? "Votre don de" : "Your gift of"}{" "}
                <span className="font-semibold text-[#002B49]">
                  {amount} {symbol}
                </span>{" "}
                {type === "monthly"
                  ? lang === "fr" ? "/ mois" : "/ month"
                  : ""}{" "}
                {lang === "fr"
                  ? "vient de transformer la vie d'une famille."
                  : "has just transformed a family's life."}
              </p>
            )}

            <p className="font-serif italic text-[#C5A059] text-lg md:text-xl mt-4">
              « {t.hero.tagline} »
            </p>

            <p className="text-[#64748B] text-sm md:text-base mt-6 max-w-lg mx-auto leading-relaxed">
              {lang === "fr"
                ? "Un email de confirmation et votre reçu fiscal vous parviendront dans les prochaines minutes."
                : "A confirmation email and your tax receipt will reach you in the next few minutes."}
            </p>
          </div>

          {/* Share */}
          <div className="mt-14 bg-white rounded-2xl border border-black/5 p-8 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.2)]">
            <div className="text-center mb-6">
              <h2 className="font-serif text-2xl md:text-3xl text-[#0A192F] font-medium">
                {lang === "fr" ? "Démultipliez votre impact" : "Multiply your impact"}
              </h2>
              <p className="text-[#64748B] text-sm mt-2">
                {lang === "fr"
                  ? "Partagez à vos proches — chaque relai compte."
                  : "Share with loved ones — every relay counts."}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {shareLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center py-4 px-3 rounded-xl border border-[#E5E7EB] hover:border-[#002B49] hover:-translate-y-0.5 transition-all bg-[#FDFBF7]"
                  data-testid={`share-${s.name.toLowerCase()}`}
                >
                  <s.Icon size={22} style={{ color: s.color }} />
                  <span className="text-xs mt-2 text-[#334155] font-medium">{s.name}</span>
                </a>
              ))}
            </div>

            <button
              onClick={copyLink}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-[#E5E7EB] hover:border-[#002B49] text-[#334155] hover:text-[#002B49] transition-all text-sm font-medium"
              data-testid="share-copy-link"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied
                ? lang === "fr" ? "Lien copié" : "Link copied"
                : lang === "fr" ? "Copier le lien" : "Copy link"}
            </button>
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate("/")}
              className="btn-outline-dark"
              data-testid="thanks-discover-more"
            >
              {lang === "fr" ? "Découvrir nos programmes" : "Discover our programs"}
            </button>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default ThankYou;
